import React, { useEffect, useRef, useState } from 'react';
import { ChromePicker } from 'react-color';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { FieldLabel, createUsePuck } from '@puckeditor/core';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import { Button, Container, Input } from 'reactstrap';
import classnames from 'classnames';

import { showModal, showModalTop } from '../../../actions';
import {
  usePageId,
  usePageBasePath,
  usePageRouteId,
  usePageVisibilityMode,
} from '../../../helpers/PageIdContext';
import { APIgetPage, APIgetPages } from '../../../api/Pages';
import { thumbnailPlaceholder } from '../../../helpers/Collections';
import { normalizeHydratedPageFile } from './Common/pageFileHydration';
import { getPageFileByUuid } from './Common/pageFiles';
import {
  getCollectionDestination,
  getFileDestination,
  normalizePageVisibilityMode,
} from '../../../helpers/PageSections';
import { isValidUrl } from '../../../helpers/Validators';
import { withLayout } from './PuckLayout';
import RadioField from './Common/RadioField';
import formatIntl from './Common/formatIntl';

const usePuckSelector = createUsePuck();

const collectionShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  uuid: PropTypes.string,
  name: PropTypes.string,
  featured_file: PropTypes.object,
});

const fileShape = PropTypes.shape({
  path: PropTypes.string,
  previewUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  uuid: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
});

const pageShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  url: PropTypes.string,
});

const pageSlugByIdCache = new Map();

const toPagePath = (pageRouteToken, pageBasePath = 'pages') => {
  if (pageRouteToken == null || pageRouteToken === '') return '';

  const routeToken = String(pageRouteToken);
  if (isValidUrl(routeToken)) return routeToken;

  if (routeToken.startsWith('/')) {
    return routeToken;
  }

  return `/${pageBasePath}/${routeToken}`;
};

const resolveButtonDestination = ({
  targetType,
  targetCollection,
  targetFile,
  targetPage,
  url,
  pageVisibilityMode,
  pageBasePath,
  pageRouteId,
  pageId,
  backUrl,
}) => {
  if (targetType === 'collection' && targetCollection?.uuid) {
    return getCollectionDestination({
      collection: targetCollection,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
    });
  }

  if (targetType === 'file' && targetFile?.uuid) {
    return getFileDestination({
      file: targetFile,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
      pageId,
      backUrl,
    });
  }

  if (targetType === 'page' && (targetPage?.url || targetPage?.id)) {
    return toPagePath(targetPage?.url || targetPage?.id, pageBasePath);
  }

  if (targetType === 'url' && url) {
    return url;
  }

  return '';
};

const isExternalDestination = (targetType, destination) => (
  targetType === 'url' && isValidUrl(destination)
);

const getButtonTargetType = (selectedItem, buttonKey) => {
  const props = selectedItem && selectedItem.props ? selectedItem.props : null;
  if (!props) return null;

  return props[`${buttonKey}TargetType`] || null;
};
const HeroButtonRadioField = ({
  id,
  name,
  value,
  onChange,
  field,
  readOnly,
}) => (
  <RadioField
    id={id}
    name={name}
    label={field.label}
    value={value}
    onChange={onChange}
    options={field.options}
    readOnly={readOnly}
  />
);

HeroButtonRadioField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

HeroButtonRadioField.defaultProps = {
  id: '',
  name: '',
  value: null,
  readOnly: false,
};

const HeroButtonCollectionField = ({
  value,
  onChange,
  field,
  onShowModal,
  buttonKey,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = getButtonTargetType(selectedItem, buttonKey);
  const intl = useIntl();

  if (targetType !== 'collection') {
    return <div className="puck-hero-button-hidden" />;
  }

  const openModal = () => {
    onShowModal('CollectionSelectModal', {
      className: 'modal-collection-select',
      backdrop: true,
      handleCollectionSelect: (selected) => {
        onChange(selected || null);
      },
    });
  };

  return (
    <div className="puck-collection-picker-label">
      <FieldLabel label={field.label}>
        {!value && (
          <Button size="sm" color="primary" onClick={openModal} className="w-100">
            <i className="far fa-folder-open mr-1" />
            {intl.formatMessage({ defaultMessage: 'Select Collection' })}
          </Button>
        )}
        {value && (
          <div className="puck-feed-collection-item">
            <img
              src={value.featured_file ? value.featured_file.urls.thumbnail : thumbnailPlaceholder}
              alt={value.name}
              className="puck-feed-collection-thumb"
            />
            <span className="puck-feed-collection-name">{value.name}</span>
            <Button
              size="sm"
              color="link"
              className="puck-feed-collection-remove"
              onClick={() => onChange(null)}
              title={intl.formatMessage({ defaultMessage: 'Remove' })}
            >
              <i className="far fa-trash mr-0" />
            </Button>
          </div>
        )}
      </FieldLabel>
    </div>
  );
};

HeroButtonCollectionField.propTypes = {
  value: collectionShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  onShowModal: PropTypes.func.isRequired,
  buttonKey: PropTypes.oneOf(['primaryButton', 'secondaryButton']).isRequired,
};

HeroButtonCollectionField.defaultProps = {
  value: null,
};

const HeroButtonFileField = ({
  value,
  onChange,
  field,
  onShowModalTop,
  buttonKey,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = getButtonTargetType(selectedItem, buttonKey);
  const intl = useIntl();

  if (targetType !== 'file') {
    return <div className="puck-hero-button-hidden" />;
  }

  const openModal = () => {
    onShowModalTop('FileSelectModalEnhanced', {
      className: 'modal-select-file modal-select-file-enhanced',
      handleSelectFile: () => { },
      handleFileData: (fileData) => {
        if (!fileData) return;
        onChange({
          uuid: fileData.uuid,
          name: fileData.original_name || fileData.name || '',
          path: fileData.path,
          previewUrl: fileData.urls?.preview,
          thumbnailUrl: fileData.urls?.thumbnail,
        });
      },
      allowUpload: false,
    });
  };

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        {value?.thumbnailUrl && (
          <div className="puck-file-preview">
            <img
              src={value.thumbnailUrl}
              alt={intl.formatMessage({ defaultMessage: 'Selected file' })}
              className="puck-file-preview-img"
            />
          </div>
        )}
        <div className="puck-file-picker-buttons">
          <Button size="sm" color="primary" onClick={openModal} className="w-100 mb-1">
            {value
              ? intl.formatMessage({ defaultMessage: 'Change file' })
              : intl.formatMessage({ defaultMessage: 'Select file' })}
          </Button>
          {value && (
            <Button size="sm" color="secondary" onClick={() => onChange(null)} className="w-100">
              {intl.formatMessage({ defaultMessage: 'Remove' })}
            </Button>
          )}
        </div>
      </FieldLabel>
    </div>
  );
};

HeroButtonFileField.propTypes = {
  value: fileShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  onShowModalTop: PropTypes.func.isRequired,
  buttonKey: PropTypes.oneOf(['primaryButton', 'secondaryButton']).isRequired,
};

HeroButtonFileField.defaultProps = {
  value: null,
};

const HeroButtonUrlField = ({
  value,
  onChange,
  field,
  buttonKey,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = getButtonTargetType(selectedItem, buttonKey);

  if (targetType !== 'url') {
    return <div className="puck-hero-button-hidden" />;
  }

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        <Input
          type="text"
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder="https://example.com"
        />
      </FieldLabel>
    </div>
  );
};

HeroButtonUrlField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  buttonKey: PropTypes.oneOf(['primaryButton', 'secondaryButton']).isRequired,
};

HeroButtonUrlField.defaultProps = {
  value: '',
};

const HeroButtonPageField = ({
  value,
  onChange,
  field,
  buttonKey,
}) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const targetType = getButtonTargetType(selectedItem, buttonKey);
  const intl = useIntl();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (targetType !== 'page') {
      return;
    }

    setLoading(true);
    APIgetPages({
      page: 1,
      per_page: 200,
      order_by: 'name',
      order: 'asc',
    })
      .then((response) => {
        const pageList = response?.data?.data || [];
        setOptions(pageList.map((page) => ({
          value: page.id,
          label: page.name || page.url || `#${page.id}`,
          page,
        })));
      })
      .catch(() => {
        setOptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [targetType]);

  if (targetType !== 'page') {
    return <div className="puck-hero-button-hidden" />;
  }

  const selectedOption = options.find((option) => option.value === value?.id) || null;

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={field.label}>
        <Select
          isClearable
          isLoading={loading}
          options={options}
          value={selectedOption}
          onChange={(selected) => {
            onChange(selected ? {
              id: selected.page.id,
              name: selected.page.name,
              url: selected.page.url,
            } : null);
          }}
          placeholder={intl.formatMessage({ defaultMessage: 'Select page...' })}
          className="page-select"
          classNamePrefix="page-select"
        />
      </FieldLabel>
    </div>
  );
};

HeroButtonPageField.propTypes = {
  value: pageShape,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  buttonKey: PropTypes.oneOf(['primaryButton', 'secondaryButton']).isRequired,
};

HeroButtonPageField.defaultProps = {
  value: null,
};

const HeroBackgroundFilePickerField = ({ value, onChange, onShowModalTop }) => {
  const intl = useIntl();
  const pageId = usePageId();
  const [displayValue, setDisplayValue] = useState(value || null);

  useEffect(() => {
    if (!value?.uuid || value?.previewUrl || value?.thumbnailUrl || !pageId) {
      setDisplayValue(value || null);
      return;
    }

    getPageFileByUuid({ pageId, fileUuid: value.uuid, fallbackFile: value })
      .then((resolved) => {
        setDisplayValue(normalizeHydratedPageFile(value, resolved || value));
      })
      .catch(() => setDisplayValue(value || null));
  }, [pageId, value]);

  const openModal = () => {
    onShowModalTop('FileSelectModalEnhanced', {
      className: 'modal-select-file modal-select-file-enhanced',
      handleSelectFile: () => { },
      handleFileData: (fileData) => {
        if (!fileData) return;
        onChange({
          path: fileData.path,
          previewUrl: fileData.urls?.preview,
          thumbnailUrl: fileData.urls?.thumbnail,
          uuid: fileData.uuid,
          width: fileData.width,
          height: fileData.height,
        });
      },
      allowUpload: false,
    });
  };

  const handleRemove = () => onChange(null);

  return (
    <div className="puck-file-field-label">
      <FieldLabel label={intl.formatMessage({ defaultMessage: 'Background image' })}>
        {(displayValue?.previewUrl || displayValue?.thumbnailUrl) && (
          <div className="puck-file-preview">
            <img
              src={displayValue?.previewUrl || displayValue?.thumbnailUrl}
              alt={intl.formatMessage({ defaultMessage: 'Selected background image' })}
              className="puck-file-preview-img"
            />
          </div>
        )}
        <div className="puck-file-picker-buttons">
          <Button size="sm" color="primary" onClick={openModal} className="w-100 mb-1">
            {value
              ? intl.formatMessage({ defaultMessage: 'Change image' })
              : intl.formatMessage({ defaultMessage: 'Select image' })}
          </Button>
          {value && (
            <Button size="sm" color="secondary" onClick={handleRemove} className="w-100">
              {intl.formatMessage({ defaultMessage: 'Remove' })}
            </Button>
          )}
        </div>
      </FieldLabel>
    </div>
  );
};

HeroBackgroundFilePickerField.propTypes = {
  value: fileShape,
  onChange: PropTypes.func.isRequired,
  onShowModalTop: PropTypes.func.isRequired,
};

HeroBackgroundFilePickerField.defaultProps = {
  value: null,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

const ConnectedHeroBackgroundFilePickerField = connect(
  null,
  mapDispatchToProps,
)(HeroBackgroundFilePickerField);

const ConnectedHeroButtonCollectionField = connect(
  null,
  mapDispatchToProps,
)(HeroButtonCollectionField);

const ConnectedHeroButtonFileField = connect(
  null,
  mapDispatchToProps,
)(HeroButtonFileField);

const OverlayOpacitySliderField = ({ value, onChange, label }) => {
  const normalized = Math.max(0, Math.min(100, Number(value) || 0));

  return (
    <div className="puck-file-field-label puck-hero-slider-field">
      <FieldLabel label={label}>
        <div className="d-flex align-items-center gap-2 puck-hero-slider-row">
          <Input
            type="range"
            min="0"
            max="100"
            step="1"
            value={normalized}
            onChange={(e) => onChange(Number(e.target.value))}
            className="puck-hero-slider"
            style={{ '--slider-progress': `${normalized}%` }}
          />
          <span className="ml-2 puck-hero-slider-value">{normalized}%</span>
        </div>
      </FieldLabel>
    </div>
  );
};

OverlayOpacitySliderField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

OverlayOpacitySliderField.defaultProps = {
  value: 0,
};

const TextColorPickerField = ({ value, onChange, label }) => {
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(value || '#222222');
  const pickerRef = useRef(null);

  const handleSwatchPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleSwatchKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    setHexInput(value || '#222222');
  }, [value]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!open) return;
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  const commitHex = (nextValue) => {
    const nextHex = nextValue.startsWith('#') ? nextValue : `#${nextValue}`;
    if (/^#[0-9a-fA-F]{6}$/.test(nextHex)) {
      onChange(nextHex);
    }
  };

  return (
    <div className="puck-file-field-label" ref={pickerRef}>
      <FieldLabel label={label}>
        <div className="d-flex align-items-center gap-2" style={{ marginTop: 4 }}>
          <div
            role="button"
            tabIndex={0}
            aria-label="Pick text color"
            className="puck-colors-swatch-btn"
            style={{ background: value || '#222222' }}
            onMouseDown={handleSwatchPointerDown}
            onTouchStart={handleSwatchPointerDown}
            onClick={() => setOpen((prev) => !prev)}
            onKeyDown={handleSwatchKeyDown}
          />
          <Input
            type="text"
            bsSize="sm"
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onBlur={(e) => commitHex(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitHex(e.currentTarget.value);
              }
            }}
            maxLength={7}
            spellCheck={false}
          />
        </div>
        {open && (
          <div style={{ marginTop: 8, position: 'relative', zIndex: 1000 }}>
            <ChromePicker
              color={value || '#222222'}
              onChange={(color) => {
                onChange(color.hex);
                setHexInput(color.hex);
              }}
              disableAlpha
            />
          </div>
        )}
      </FieldLabel>
    </div>
  );
};

TextColorPickerField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

TextColorPickerField.defaultProps = {
  value: '#222222',
};

const PuckHeroRender = ({
  title,
  description,
  align,
  size,
  imageUrl,
  imageFile,
  textColor,
  overlayOpacity,
  primaryButtonLabel,
  primaryButtonTargetType,
  primaryButtonTargetCollection,
  primaryButtonTargetFile,
  primaryButtonTargetPage,
  primaryButtonUrl,
  secondaryButtonLabel,
  secondaryButtonTargetType,
  secondaryButtonTargetCollection,
  secondaryButtonTargetFile,
  secondaryButtonTargetPage,
  secondaryButtonUrl,
  page_visibility_mode: pageVisibilityMode,
  puck,
}) => {
  const location = useLocation();
  const pageId = usePageId();
  const pageBasePath = usePageBasePath();
  const pageRouteId = usePageRouteId();
  const contextVisibilityMode = usePageVisibilityMode();
  const effectivePageVisibilityMode = normalizePageVisibilityMode(
    pageVisibilityMode || contextVisibilityMode,
  );
  const backUrl = `${location.pathname}${location.search || ''}`;
  const isEditing = puck?.isEditing;
  const [resolvedImageFile, setResolvedImageFile] = useState(null);
  const [resolvedPrimaryButtonFile, setResolvedPrimaryButtonFile] = useState(
    primaryButtonTargetFile || null,
  );
  const [resolvedSecondaryButtonFile, setResolvedSecondaryButtonFile] = useState(
    secondaryButtonTargetFile || null,
  );
  const [resolvedPrimaryButtonPage, setResolvedPrimaryButtonPage] = useState(
    primaryButtonTargetPage || null,
  );
  const [resolvedSecondaryButtonPage, setResolvedSecondaryButtonPage] = useState(
    secondaryButtonTargetPage || null,
  );

  useEffect(() => {
    if (isEditing || !imageFile?.uuid || !pageId) {
      setResolvedImageFile(imageFile || null);
      return;
    }

    getPageFileByUuid({ pageId, fileUuid: imageFile.uuid, fallbackFile: imageFile })
      .then((resolved) => {
        setResolvedImageFile(normalizeHydratedPageFile(imageFile, resolved || imageFile));
      })
      .catch(() => setResolvedImageFile(imageFile || null));
  }, [pageId, imageFile, isEditing]);

  useEffect(() => {
    let mounted = true;

    if (primaryButtonTargetType !== 'file' || !primaryButtonTargetFile?.uuid || !pageId) {
      setResolvedPrimaryButtonFile(primaryButtonTargetFile || null);
      return () => {
        mounted = false;
      };
    }

    getPageFileByUuid({
      pageId,
      fileUuid: primaryButtonTargetFile.uuid,
      fallbackFile: primaryButtonTargetFile,
    })
      .then((resolved) => {
        if (!mounted) {
          return;
        }

        setResolvedPrimaryButtonFile(
          normalizeHydratedPageFile(primaryButtonTargetFile, resolved || primaryButtonTargetFile),
        );
      })
      .catch(() => {
        if (!mounted) {
          return;
        }

        setResolvedPrimaryButtonFile(primaryButtonTargetFile || null);
      });

    return () => {
      mounted = false;
    };
  }, [pageId, primaryButtonTargetType, primaryButtonTargetFile]);

  useEffect(() => {
    let mounted = true;

    if (secondaryButtonTargetType !== 'file' || !secondaryButtonTargetFile?.uuid || !pageId) {
      setResolvedSecondaryButtonFile(secondaryButtonTargetFile || null);
      return () => {
        mounted = false;
      };
    }

    getPageFileByUuid({
      pageId,
      fileUuid: secondaryButtonTargetFile.uuid,
      fallbackFile: secondaryButtonTargetFile,
    })
      .then((resolved) => {
        if (!mounted) {
          return;
        }

        setResolvedSecondaryButtonFile(
          normalizeHydratedPageFile(
            secondaryButtonTargetFile,
            resolved || secondaryButtonTargetFile,
          ),
        );
      })
      .catch(() => {
        if (!mounted) {
          return;
        }

        setResolvedSecondaryButtonFile(secondaryButtonTargetFile || null);
      });

    return () => {
      mounted = false;
    };
  }, [pageId, secondaryButtonTargetType, secondaryButtonTargetFile]);

  useEffect(() => {
    let mounted = true;

    if (primaryButtonTargetType !== 'page' || !primaryButtonTargetPage) {
      setResolvedPrimaryButtonPage(primaryButtonTargetPage || null);
      return () => {
        mounted = false;
      };
    }

    const targetPageId = primaryButtonTargetPage.id;
    if (!targetPageId) {
      setResolvedPrimaryButtonPage(primaryButtonTargetPage);
      return () => {
        mounted = false;
      };
    }

    const cacheKey = String(targetPageId);
    const cachedSlug = pageSlugByIdCache.get(cacheKey);

    if (cachedSlug) {
      setResolvedPrimaryButtonPage({
        ...primaryButtonTargetPage,
        url: cachedSlug,
      });
      return () => {
        mounted = false;
      };
    }

    APIgetPage(targetPageId, isEditing ? { edit_mode: true } : {})
      .then((response) => {
        if (!mounted) {
          return;
        }

        const currentSlug = response?.data?.url;
        if (currentSlug) {
          pageSlugByIdCache.set(cacheKey, currentSlug);
        }

        setResolvedPrimaryButtonPage({
          ...primaryButtonTargetPage,
          url: currentSlug || primaryButtonTargetPage.url,
        });
      })
      .catch(() => {
        if (!mounted) {
          return;
        }

        setResolvedPrimaryButtonPage(primaryButtonTargetPage);
      });

    return () => {
      mounted = false;
    };
  }, [primaryButtonTargetType, primaryButtonTargetPage, isEditing]);

  useEffect(() => {
    let mounted = true;

    if (secondaryButtonTargetType !== 'page' || !secondaryButtonTargetPage) {
      setResolvedSecondaryButtonPage(secondaryButtonTargetPage || null);
      return () => {
        mounted = false;
      };
    }

    const targetPageId = secondaryButtonTargetPage.id;
    if (!targetPageId) {
      setResolvedSecondaryButtonPage(secondaryButtonTargetPage);
      return () => {
        mounted = false;
      };
    }

    const cacheKey = String(targetPageId);
    const cachedSlug = pageSlugByIdCache.get(cacheKey);

    if (cachedSlug) {
      setResolvedSecondaryButtonPage({
        ...secondaryButtonTargetPage,
        url: cachedSlug,
      });
      return () => {
        mounted = false;
      };
    }

    APIgetPage(targetPageId, isEditing ? { edit_mode: true } : {})
      .then((response) => {
        if (!mounted) {
          return;
        }

        const currentSlug = response?.data?.url;
        if (currentSlug) {
          pageSlugByIdCache.set(cacheKey, currentSlug);
        }

        setResolvedSecondaryButtonPage({
          ...secondaryButtonTargetPage,
          url: currentSlug || secondaryButtonTargetPage.url,
        });
      })
      .catch(() => {
        if (!mounted) {
          return;
        }

        setResolvedSecondaryButtonPage(secondaryButtonTargetPage);
      });

    return () => {
      mounted = false;
    };
  }, [secondaryButtonTargetType, secondaryButtonTargetPage, isEditing]);

  const displayImageFile = isEditing ? imageFile : resolvedImageFile;
  const imageSrc = displayImageFile?.previewUrl
    || displayImageFile?.thumbnailUrl
    || displayImageFile?.url
    || imageUrl;
  const overlayOpacityClamped = Math.max(0, Math.min(100, Number(overlayOpacity) || 0));
  const hasBackgroundImage = !!imageSrc;
  const primaryDestination = resolveButtonDestination({
    targetType: primaryButtonTargetType || 'url',
    targetCollection: primaryButtonTargetCollection,
    targetFile: primaryButtonTargetType === 'file'
      ? resolvedPrimaryButtonFile
      : primaryButtonTargetFile,
    targetPage: primaryButtonTargetType === 'page'
      ? resolvedPrimaryButtonPage
      : primaryButtonTargetPage,
    url: primaryButtonUrl,
    pageVisibilityMode: effectivePageVisibilityMode,
    pageBasePath,
    pageRouteId,
    pageId,
    backUrl,
  });
  const secondaryDestination = resolveButtonDestination({
    targetType: secondaryButtonTargetType || 'url',
    targetCollection: secondaryButtonTargetCollection,
    targetFile: secondaryButtonTargetType === 'file'
      ? resolvedSecondaryButtonFile
      : secondaryButtonTargetFile,
    targetPage: secondaryButtonTargetType === 'page'
      ? resolvedSecondaryButtonPage
      : secondaryButtonTargetPage,
    url: secondaryButtonUrl,
    pageVisibilityMode: effectivePageVisibilityMode,
    pageBasePath,
    pageRouteId,
    pageId,
    backUrl,
  });
  const primaryExternal = isExternalDestination(primaryButtonTargetType || 'url', primaryDestination);
  const secondaryExternal = isExternalDestination(secondaryButtonTargetType || 'url', secondaryDestination);
  const secondaryButtonStyle = {
    color: textColor || undefined,
    borderColor: textColor || undefined,
  };

  const renderPrimaryButton = () => {
    if (!primaryButtonLabel) {
      return null;
    }

    if (!primaryDestination) {
      return (
        <Button color="primary" size="lg">
          {primaryButtonLabel}
        </Button>
      );
    }

    if (primaryExternal) {
      return (
        <Button color="primary" size="lg" href={primaryDestination} target="_blank" rel="noopener noreferrer">
          {primaryButtonLabel}
        </Button>
      );
    }

    return (
      <Button color="primary" size="lg" tag={Link} to={primaryDestination}>
        {primaryButtonLabel}
      </Button>
    );
  };

  const renderSecondaryButton = () => {
    if (!secondaryButtonLabel) {
      return null;
    }

    if (!secondaryDestination) {
      return (
        <Button
          className="ml-2"
          color="secondary"
          outline
          size="lg"
          style={secondaryButtonStyle}
        >
          {secondaryButtonLabel}
        </Button>
      );
    }

    if (secondaryExternal) {
      return (
        <Button
          className="ml-2"
          color="secondary"
          outline
          size="lg"
          href={secondaryDestination}
          target="_blank"
          rel="noopener noreferrer"
          style={secondaryButtonStyle}
        >
          {secondaryButtonLabel}
        </Button>
      );
    }

    return (
      <Button
        className="ml-2"
        color="secondary"
        outline
        size="lg"
        tag={Link}
        to={secondaryDestination}
        style={secondaryButtonStyle}
      >
        {secondaryButtonLabel}
      </Button>
    );
  };

  return (
    <section
      className={classnames('custom-page-section puck-hero', {
        'puck-hero--with-bg': hasBackgroundImage,
        'puck-hero-medium': size === 'medium',
        'puck-hero-large': size === 'large',
      })}
      style={hasBackgroundImage ? { backgroundImage: `url('${imageSrc}')` } : undefined}
    >
      {hasBackgroundImage && overlayOpacityClamped > 0 && (
        <div
          className="puck-hero-overlay"
          style={{ opacity: overlayOpacityClamped / 100 }}
          aria-hidden="true"
        />
      )}
      <Container className="puck-hero-container">
        <div
          className={classnames('puck-hero-inner', {
            'puck-hero-inner-center': align === 'center',
            'puck-hero-inner-left': align === 'left',
            'puck-hero-inner-right': align === 'right',
          })}
        >
          <div className="puck-hero-content">
            {title && <h1 style={{ color: textColor || undefined }}>{title}</h1>}
            {description && (
              <p className="puck-hero-description" style={{ color: textColor || undefined }}>
                {description}
              </p>
            )}
            {(primaryButtonLabel || secondaryButtonLabel) && (
              <div className="puck-hero-actions">
                {renderPrimaryButton()}
                {renderSecondaryButton()}
              </div>
            )}
          </div>

        </div>
      </Container>
    </section>
  );
};

PuckHeroRender.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  size: PropTypes.string,
  imageUrl: PropTypes.string,
  imageFile: fileShape,
  textColor: PropTypes.string,
  overlayOpacity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  primaryButtonLabel: PropTypes.string,
  primaryButtonTargetType: PropTypes.oneOf(['collection', 'file', 'url', 'page']),
  primaryButtonTargetCollection: collectionShape,
  primaryButtonTargetFile: fileShape,
  primaryButtonTargetPage: pageShape,
  primaryButtonUrl: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  secondaryButtonTargetType: PropTypes.oneOf(['collection', 'file', 'url', 'page']),
  secondaryButtonTargetCollection: collectionShape,
  secondaryButtonTargetFile: fileShape,
  secondaryButtonTargetPage: pageShape,
  secondaryButtonUrl: PropTypes.string,
  page_visibility_mode: PropTypes.string,
  puck: PropTypes.shape({
    isEditing: PropTypes.bool,
  }),
};

PuckHeroRender.defaultProps = {
  title: '',
  description: '',
  align: 'left',
  size: 'small',
  imageUrl: '',
  imageFile: null,
  textColor: '#222222',
  overlayOpacity: 35,
  primaryButtonLabel: '',
  primaryButtonTargetType: 'url',
  primaryButtonTargetCollection: null,
  primaryButtonTargetFile: null,
  primaryButtonTargetPage: null,
  primaryButtonUrl: '',
  secondaryButtonLabel: '',
  secondaryButtonTargetType: 'url',
  secondaryButtonTargetCollection: null,
  secondaryButtonTargetFile: null,
  secondaryButtonTargetPage: null,
  secondaryButtonUrl: '',
  page_visibility_mode: null,
  puck: null,
};

const PuckHero = {
  get label() { return formatIntl('Hero'); },
  fields: {
    title: {
      type: 'text',
      get label() { return formatIntl('Title'); },
    },
    description: {
      type: 'textarea',
      get label() { return formatIntl('Description'); },
    },
    align: {
      type: 'radio',
      get label() { return formatIntl('Align'); },
      options: [
        { get label() { return formatIntl('Left'); }, value: 'left' },
        { get label() { return formatIntl('Center'); }, value: 'center' },
        { get label() { return formatIntl('Right'); }, value: 'right' },
      ],
    },
    size: {
      type: 'radio',
      get label() { return formatIntl('Size'); },
      options: [
        { get label() { return formatIntl('Small'); }, value: 'small' },
        { get label() { return formatIntl('Medium'); }, value: 'medium' },
        { get label() { return formatIntl('Large'); }, value: 'large' },
      ],
    },
    imageFile: {
      type: 'custom',
      get label() { return formatIntl('Background image'); },
      render: (props) => <ConnectedHeroBackgroundFilePickerField {...props} />,
    },
    overlayOpacity: {
      type: 'custom',
      get label() { return formatIntl('Background overlay opacity'); },
      render: ({ value, onChange, field }) => (
        <OverlayOpacitySliderField
          value={value}
          onChange={onChange}
          label={field.label}
        />
      ),
    },
    textColor: {
      type: 'custom',
      get label() { return formatIntl('Text color'); },
      render: ({ value, onChange, field }) => (
        <TextColorPickerField
          value={value}
          onChange={onChange}
          label={field.label}
        />
      ),
    },
    primaryButtonLabel: {
      type: 'text',
      get label() { return formatIntl('Primary button label'); },
    },
    primaryButtonTargetType: {
      type: 'custom',
      get label() { return formatIntl('Primary button destination'); },
      options: [
        { get label() { return formatIntl('Collection'); }, value: 'collection' },
        { get label() { return formatIntl('File'); }, value: 'file' },
        { get label() { return formatIntl('URL'); }, value: 'url' },
        { get label() { return formatIntl('Page'); }, value: 'page' },
      ],
      render: (props) => (
        <div className="puck-hero-target-type-field">
          <HeroButtonRadioField {...props} />
        </div>
      ),
    },
    primaryButtonTargetCollection: {
      type: 'custom',
      get label() { return formatIntl('Primary button collection'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <ConnectedHeroButtonCollectionField {...props} buttonKey="primaryButton" />
        </div>
      ),
    },
    primaryButtonTargetFile: {
      type: 'custom',
      get label() { return formatIntl('Primary button file'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <ConnectedHeroButtonFileField {...props} buttonKey="primaryButton" />
        </div>
      ),
    },
    primaryButtonUrl: {
      type: 'custom',
      get label() { return formatIntl('Primary button URL'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <HeroButtonUrlField {...props} buttonKey="primaryButton" />
        </div>
      ),
    },
    primaryButtonTargetPage: {
      type: 'custom',
      get label() { return formatIntl('Primary button page'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <HeroButtonPageField {...props} buttonKey="primaryButton" />
        </div>
      ),
    },
    secondaryButtonLabel: {
      type: 'text',
      get label() { return formatIntl('Secondary button label'); },
    },
    secondaryButtonTargetType: {
      type: 'custom',
      get label() { return formatIntl('Secondary button destination'); },
      options: [
        { get label() { return formatIntl('Collection'); }, value: 'collection' },
        { get label() { return formatIntl('File'); }, value: 'file' },
        { get label() { return formatIntl('URL'); }, value: 'url' },
        { get label() { return formatIntl('Page'); }, value: 'page' },
      ],
      render: (props) => (
        <div className="puck-hero-target-type-field">
          <HeroButtonRadioField {...props} />
        </div>
      ),
    },
    secondaryButtonTargetCollection: {
      type: 'custom',
      get label() { return formatIntl('Secondary button collection'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <ConnectedHeroButtonCollectionField {...props} buttonKey="secondaryButton" />
        </div>
      ),
    },
    secondaryButtonTargetFile: {
      type: 'custom',
      get label() { return formatIntl('Secondary button file'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <ConnectedHeroButtonFileField {...props} buttonKey="secondaryButton" />
        </div>
      ),
    },
    secondaryButtonUrl: {
      type: 'custom',
      get label() { return formatIntl('Secondary button URL'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <HeroButtonUrlField {...props} buttonKey="secondaryButton" />
        </div>
      ),
    },
    secondaryButtonTargetPage: {
      type: 'custom',
      get label() { return formatIntl('Secondary button page'); },
      render: (props) => (
        <div className="puck-hero-target-field">
          <HeroButtonPageField {...props} buttonKey="secondaryButton" />
        </div>
      ),
    },
  },

  get defaultProps() {
    return {
      title: formatIntl('Hero title'),
      description: formatIntl('Hero description'),
      align: 'left',
      size: 'small',
      imageUrl: '',
      imageFile: null,
      textColor: '#222222',
      overlayOpacity: 35,
      primaryButtonLabel: formatIntl('Learn more'),
      primaryButtonTargetType: 'url',
      primaryButtonTargetCollection: null,
      primaryButtonTargetFile: null,
      primaryButtonTargetPage: null,
      primaryButtonUrl: '',
      secondaryButtonLabel: '',
      secondaryButtonTargetType: 'url',
      secondaryButtonTargetCollection: null,
      secondaryButtonTargetFile: null,
      secondaryButtonTargetPage: null,
      secondaryButtonUrl: '',
      layout: {
        padding: '0px',
      },
    };
  },

  render: (props) => <PuckHeroRender {...props} />,
};

export default withLayout(PuckHero);
